import * as React from 'react';
import { ThemeProvider as BaseThemeProvider } from '@theme-ui/core';
import { Global } from '@emotion/react';
import { css } from '@theme-ui/css';

export type WithThemeProps = {
  theme?: any;
};

function themeToVars(category: string, entries: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(entries).map(([key, value]) => [`--${category}-${key}`, value])
  );
}

export type ThemeProviderProps = React.PropsWithChildren<{
  theme?: $TSFixMe;
}>;

export function ThemeProvider(props: ThemeProviderProps) {
  const theme = React.useMemo(
    () => ({
      ...props.theme?.designTokens,
      colors: {
        ...props.theme.colors,
        ...props.theme?.designTokens.colors
      }
    }),
    [props.theme]
  );

  const applyRootStyles = theme.config?.applyRootStyles ?? true;
  const styles = React.useMemo(() => {
    const colorVars = themeToVars('color', theme.colors);
    const fontVars = themeToVars('font', theme.fonts);
    const { styles: { root, ...styles } = { root: undefined } } = theme;
    if (applyRootStyles) {
      return {
        ':root': {
          ...colorVars,
          ...fontVars,
          ...root
        },
        ...styles
      };
    }
    return {};
  }, [theme, applyRootStyles]);
  return (
    <BaseThemeProvider theme={theme}>
      <Global styles={css(styles)} />
      {props.children}
    </BaseThemeProvider>
  );
}

export function withTheme<T>(WrappedComponent: React.ComponentType<T>) {
  // Try to create a nice displayName for React Dev Tools.
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithTheme: React.FunctionComponent<T & WithThemeProps> = ({
    theme,
    ...props
  }: WithThemeProps) => {
    return (
      <ThemeProvider theme={theme}>
        <WrappedComponent {...(props as T & WithThemeProps)} theme={theme} />
      </ThemeProvider>
    );
  };

  ComponentWithTheme.displayName = `withTheme(${displayName})`;

  return ComponentWithTheme;
}
